// Left-edge sidebar listing the provider's cloud resource catalog.
// Items are draggable onto valid parent nodes on the canvas, AND single-click
// to begin a "click-to-place" flow.

import { useEffect, useMemo, useState } from "react";
import { Search, X, GripVertical } from "lucide-react";
import { api } from "../../lib/api";
import { PROVIDER_LABELS } from "../../lib/archModel";
import { categoryToTier } from "../../lib/archReducer";

const PANEL_WIDTH = 320;

export default function ResourceSidebar({
  provider,
  open,
  onClose,
  onBeginDrag,
  onEndDrag,
  onSelectForPlacement,
  pendingPlacement,
}) {
  const [items, setItems] = useState([]);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !provider) return;
    let cancelled = false;
    setLoading(true);
    api
      .get(`/cloud-resources?provider=${provider}`)
      .then(({ data }) => {
        if (!cancelled) setItems(data.items || []);
      })
      .catch(() => !cancelled && setItems([]))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [open, provider]);

  const grouped = useMemo(() => {
    const q = query.trim().toLowerCase();
    const filtered = q
      ? items.filter(
          (it) =>
            it.name.toLowerCase().includes(q) ||
            (it.description || "").toLowerCase().includes(q) ||
            (it.category || "").toLowerCase().includes(q)
        )
      : items;
    const by = new Map();
    for (const it of filtered) {
      const k = it.category || "Other";
      if (!by.has(k)) by.set(k, []);
      by.get(k).push(it);
    }
    return Array.from(by.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [items, query]);

  if (!open) return null;

  return (
    <aside
      data-testid="resource-sidebar"
      style={{
        position: "absolute",
        left: 0,
        top: 0,
        bottom: 0,
        width: PANEL_WIDTH,
        background: "var(--color-background-secondary)",
        borderRight: "1px solid var(--color-border-primary)",
        zIndex: 20,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <header
        style={{
          padding: "14px 16px",
          borderBottom: "1px solid var(--color-border-primary)",
        }}
      >
        <div className="flex items-center justify-between gap-2">
          <div>
            <p
              className="font-mono"
              style={{
                fontSize: 10,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                color: "var(--color-text-tertiary)",
                marginBottom: 2,
              }}
            >
              Add Resource
            </p>
            <h3
              style={{
                fontSize: 14,
                fontWeight: 600,
                color: "var(--color-text-primary)",
              }}
            >
              {PROVIDER_LABELS[provider] || provider} catalog
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="resource-sidebar-close"
            className="motion-micro"
            style={{
              width: 28,
              height: 28,
              border: "1px solid var(--color-border-primary)",
              borderRadius: 6,
              background: "var(--color-background-tertiary)",
              color: "var(--color-text-secondary)",
              cursor: "pointer",
            }}
          >
            <X size={14} style={{ margin: "0 auto" }} />
          </button>
        </div>
        <div
          className="mt-3 flex items-center gap-2"
          style={{
            background: "var(--color-background-primary)",
            border: "1px solid var(--color-border-primary)",
            borderRadius: 8,
            padding: "7px 10px",
          }}
        >
          <Search size={13} color="var(--color-text-tertiary)" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search 500+ services"
            data-testid="resource-sidebar-search"
            style={{
              background: "transparent",
              border: "none",
              outline: "none",
              color: "var(--color-text-primary)",
              fontSize: 13,
              width: "100%",
            }}
          />
        </div>
      </header>

      {pendingPlacement && (
        <div
          data-testid="resource-sidebar-placement-banner"
          style={{
            padding: "10px 14px",
            background: "color-mix(in oklab, var(--color-accent) 12%, transparent)",
            borderBottom: "1px solid color-mix(in oklab, var(--color-accent) 45%, transparent)",
            color: "var(--color-accent)",
            fontSize: 11.5,
            lineHeight: 1.4,
          }}
        >
          Click a highlighted node to place <strong>{pendingPlacement.name}</strong>.
          ESC to cancel.
        </div>
      )}

      <div
        style={{ flex: 1, overflowY: "auto", padding: "10px 12px" }}
        data-testid="resource-sidebar-list"
      >
        {loading && (
          <p style={{ fontSize: 12, color: "var(--color-text-tertiary)", padding: "8px 4px" }}>
            Loading catalog…
          </p>
        )}
        {!loading && grouped.length === 0 && (
          <p style={{ fontSize: 12, color: "var(--color-text-tertiary)", padding: "8px 4px" }}>
            No resources match &quot;{query}&quot;.
          </p>
        )}
        {grouped.map(([cat, list]) => (
          <section key={cat} style={{ marginBottom: 14 }}>
            <h4
              className="font-mono"
              style={{
                fontSize: 10,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                color: "var(--color-text-tertiary)",
                marginBottom: 6,
                padding: "0 4px",
              }}
            >
              {cat}
            </h4>
            <ul style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              {list.map((it) => (
                <li key={`${it.provider}-${cat}-${it.name}`}>
                  <button
                    type="button"
                    draggable
                    data-testid={`resource-item-${it.name}`}
                    onDragStart={(e) => {
                      const payload = {
                        kind: "new-resource",
                        name: it.name,
                        category: cat,
                        tier: categoryToTier(cat),
                        description: it.description || "",
                      };
                      e.dataTransfer.setData("application/arch-payload", JSON.stringify(payload));
                      e.dataTransfer.effectAllowed = "copy";
                      onBeginDrag?.(payload);
                    }}
                    onDragEnd={() => onEndDrag?.()}
                    onClick={() =>
                      onSelectForPlacement?.({
                        kind: "new-resource",
                        name: it.name,
                        category: cat,
                        tier: categoryToTier(cat),
                        description: it.description || "",
                      })
                    }
                    className="motion-micro"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      width: "100%",
                      textAlign: "left",
                      background: "transparent",
                      border: "1px solid transparent",
                      color: "var(--color-text-primary)",
                      fontSize: 12.5,
                      padding: "8px 10px",
                      borderRadius: 8,
                      cursor: "grab",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = "var(--color-background-tertiary)";
                      e.currentTarget.style.borderColor = "var(--color-border-primary)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = "transparent";
                      e.currentTarget.style.borderColor = "transparent";
                    }}
                  >
                    <GripVertical size={12} color="var(--color-text-tertiary)" />
                    <span style={{ flex: 1, minWidth: 0 }} className="truncate" title={it.name}>
                      {it.name}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </section>
        ))}
      </div>
    </aside>
  );
}

ResourceSidebar.WIDTH = PANEL_WIDTH;
